import { useEffect, useState } from "react";
import { History, KeyRound, MessageCircle, Search, ShieldAlert, Unlink, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import { ChatPanel } from "../components/ChatPanel";
import { RouterCombobox } from "../components/RouterCombobox";
import PinConfirm from "../components/PinConfirm";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const fmtBytes = (b) => {
  if (!b) return "0 MB";
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(2)} GB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

export default function Complaints() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const { selected, router, routers, setSelected } = useRouters();
  const [code, setCode] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lepasBusy, setLepasBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [gantiOpen, setGantiOpen] = useState(false);
  const [gantiForm, setGantiForm] = useState({ new_code: "", limit_uptime: "", limit_mb: "" });
  const [pinGantiOpen, setPinGantiOpen] = useState(false);
  const [gantiBusy, setGantiBusy] = useState(false);
  const [logs, setLogs] = useState([]);
  const [unreadChat, setUnreadChat] = useState(0);

  useEffect(() => {
    if (!me?.id || isAdmin) return undefined;
    const loadUnread = () => api.get("/chat/unread").then((r) => setUnreadChat(r.data[`agent:${me.id}`] || 0)).catch(() => {});
    loadUnread();
    const t = setInterval(loadUnread, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  const loadLogs = () => api.get("/mac-unbind/logs").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { loadLogs(); }, []);
  useEffect(() => { setDetail(null); }, [selected]);

  const cari = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/routers/${selected}/vouchers/${encodeURIComponent(code.trim())}/detail`);
      setDetail(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const lepas = async () => {
    setLepasBusy(true);
    try {
      const { data } = await api.post(`/routers/${selected}/vouchers/${encodeURIComponent(detail.name)}/lepas-mac`);
      toast.success(`MAC ${data.old_mac || "-"} dilepas dari ${detail.name}${data.kicked ? ` • ${data.kicked} sesi aktif diputus` : ""}. Pelanggan bisa login ulang.`);
      setDetail({ ...detail, mac_address: "", active_sessions: 0 });
      loadLogs();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLepasBusy(false);
    }
  };

  const onLepasClick = () => {
    if (isAdmin) { setPinOpen(true); return; }
    lepas();
  };

  const doGanti = async () => {
    setGantiBusy(true);
    try {
      const { data } = await api.post(`/routers/${selected}/vouchers/${encodeURIComponent(detail.name)}/ganti-kode`, { new_code: gantiForm.new_code, limit_uptime: gantiForm.limit_uptime, limit_mb: Number(gantiForm.limit_mb) || 0 });
      toast.success(`Kode baru dibuat: ${data.new_code} (profile ${data.profile}) — kode lama ${detail.name} dinonaktifkan`);
      setGantiOpen(false);
      setCode(data.new_code);
      setDetail(null);
      const { data: det } = await api.get(`/routers/${selected}/vouchers/${encodeURIComponent(data.new_code)}/detail`);
      setDetail(det);
    } catch (err) { toast.error(errMsg(err)); } finally { setGantiBusy(false); }
  };

  const routerPicker = (
    <Card className="bg-card border-border shadow-sm" data-testid="router-pick-card">
      <CardContent className="p-5 space-y-1.5">
        <Label>Cabang / Router yang Dikomplain</Label>
        <RouterCombobox routers={routers} value={selected || ""} onChange={setSelected} placeholder="Pilih cabang/router" testid="select-komplain-router" className="h-10 text-sm" />
      </CardContent>
    </Card>
  );

  if (!selected) {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="komplain-no-router">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Komplain Voucher</h1>
          <p className="text-sm text-muted-foreground mt-1">Pilih cabang/router yang akan dikomplain terlebih dahulu.</p>
        </div>
        {routerPicker}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="komplain-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Komplain Voucher</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Voucher tidak bisa login di perangkat lain? Ketik kode voucher — sistem membuka datanya di MikroTik <span className="font-medium text-foreground">({router?.name})</span> dan melepas MAC Address yang menempel.
        </p>
      </div>

      {routerPicker}

      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-5">
          <form onSubmit={cari} className="flex gap-2 items-end">
            <div className="space-y-1.5 flex-1">
              <Label>Kode Voucher</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="contoh: AB12CD" className="font-mono" data-testid="input-kode-voucher" />
            </div>
            <Button type="submit" disabled={loading} data-testid="btn-cari-voucher">
              <Search className="w-4 h-4 mr-1.5" /> {loading ? "Mencari..." : "Cari"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {detail && (
        <Card className="bg-card border-border shadow-sm" data-testid="voucher-detail-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Wifi className="w-4 h-4 text-sky-500" /> Data Voucher: <span className="font-mono">{detail.name}</span></CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="text-muted-foreground">Profile</div><div className="font-mono text-right" data-testid="detail-profile">{detail.profile || "-"}</div>
              <div className="text-muted-foreground">Server</div><div className="font-mono text-right">{detail.server || "-"}</div>
              <div className="text-muted-foreground">Status</div>
              <div className="text-right" data-testid="detail-status">
                <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${detail.disabled ? "bg-rose-500/10 text-rose-400 border-rose-500/20" : "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"}`}>
                  {detail.disabled ? "Disabled" : "Enable"}
                </span>
              </div>
              <div className="text-muted-foreground">Uptime terpakai</div><div className="font-mono text-right">{detail.uptime || "0s"}</div>
              <div className="text-muted-foreground">Tanggal Login</div><div className="font-mono text-right" data-testid="detail-used-at">{detail.used_at ? fmtDateTime(detail.used_at) : "Belum pernah login"}</div>
              <div className="text-muted-foreground">Pemakaian data</div><div className="font-mono text-right">{fmtBytes(detail.bytes_in + detail.bytes_out)}</div>
              <div className="text-muted-foreground">Sesi aktif</div><div className="font-mono text-right" data-testid="detail-sessions">{detail.active_sessions}</div>
              {detail.comment && (<><div className="text-muted-foreground">Komentar</div><div className="font-mono text-right text-xs">{detail.comment}</div></>)}
            </div>

            {detail.mac_address ? (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 space-y-3" data-testid="mac-bound-box">
                <div className="flex items-center gap-2 text-sm">
                  <ShieldAlert className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>Voucher ini <span className="font-semibold">terkunci</span> ke perangkat dengan MAC:</span>
                </div>
                <div className="font-mono font-bold text-lg text-rose-500 text-center tracking-wider" data-testid="detail-mac">{detail.mac_address}</div>
                <Button variant="destructive" className="w-full" onClick={onLepasClick} disabled={lepasBusy} data-testid="btn-lepas-mac">
                  <Unlink className="w-4 h-4 mr-1.5" /> {lepasBusy ? "Melepas..." : "Hapus MAC Address"}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">MAC akan dilepas & sesi aktif diputus — pelanggan bisa login ulang di perangkat baru.</p>
              </div>
            ) : (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center text-sm text-emerald-600" data-testid="mac-free-box">
                Tidak ada MAC Address yang menempel — voucher bebas dipakai di perangkat manapun.
              </div>
            )}
            {isAdmin && (
              <Button variant="outline" className="w-full text-amber-600 border-amber-500/40 hover:bg-amber-500/10" onClick={() => { setGantiForm({ new_code: "", limit_uptime: "", limit_mb: "" }); setGantiOpen(true); }} data-testid="btn-ganti-kode">
                <KeyRound className="w-4 h-4 mr-1.5" /> Ganti Kode Voucher
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!isAdmin && me?.id && (
        <Card className="bg-card border-border shadow-sm" data-testid="chat-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><span className="relative inline-flex"><MessageCircle className="w-4 h-4 text-sky-500" />{unreadChat > 0 && <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="chat-unread-badge">{unreadChat}</span>}</span> Chat dengan Admin</CardTitle></CardHeader>
          <CardContent><ChatPanel channel={`agent:${me.id}`} testid="voucher-chat" /></CardContent>
        </Card>
      )}

      <Card className="bg-card border-border shadow-sm" data-testid="mac-logs-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><History className="w-4 h-4 text-sky-500" /> Riwayat Pelepasan MAC</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border max-h-64 overflow-y-auto" data-testid="mac-logs">
            {logs.map((l) => (
              <div key={l.id} className="px-4 py-2.5 text-xs flex items-center gap-3" data-testid={`mac-log-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">Voucher <span className="font-mono">{l.voucher}</span> — MAC <span className="font-mono">{l.old_mac || "-"}</span> dilepas</div>
                  <div className="text-muted-foreground font-mono">{fmtDateTime(l.created_at)} • {l.router_name || ""} • oleh {l.by}{l.kicked ? ` • ${l.kicked} sesi diputus` : ""}</div>
                </div>
              </div>
            ))}
            {!logs.length && <div className="px-4 py-8 text-center text-xs text-muted-foreground">Belum ada riwayat pelepasan MAC</div>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={gantiOpen} onOpenChange={setGantiOpen}>
        <DialogContent data-testid="dialog-ganti-kode">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-500" /> Ganti Kode Voucher</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            Kode lama <span className="font-mono font-semibold text-foreground">{detail?.name}</span> akan dinonaktifkan. Kode baru dibuat dengan profile yang sama (<span className="font-mono text-foreground">{detail?.profile}</span>) — masa berlaku & kuota ditentukan admin sesuai perkiraan sisa pemakaian voucher lama.
          </p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Kode Baru (kosongkan untuk generate otomatis)</Label>
              <Input value={gantiForm.new_code} onChange={(e) => setGantiForm({ ...gantiForm, new_code: e.target.value.toUpperCase() })} placeholder="cth: AB12CD" className="font-mono" data-testid="input-ganti-code" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Masa Berlaku</Label>
                <Input value={gantiForm.limit_uptime} onChange={(e) => setGantiForm({ ...gantiForm, limit_uptime: e.target.value })} placeholder="cth: 2h / 1d / 30d" data-testid="input-ganti-uptime" />
              </div>
              <div className="space-y-1.5">
                <Label>Kuota (MB, 0 = unlimited)</Label>
                <Input type="number" min="0" value={gantiForm.limit_mb} onChange={(e) => setGantiForm({ ...gantiForm, limit_mb: e.target.value })} placeholder="cth: 500" data-testid="input-ganti-quota" />
              </div>
            </div>
            <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => { setGantiOpen(false); setPinGantiOpen(true); }} disabled={gantiBusy} data-testid="btn-confirm-ganti">
              <KeyRound className="w-4 h-4 mr-1.5" /> Ganti Kode
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PinConfirm
        open={pinGantiOpen}
        onOpenChange={setPinGantiOpen}
        title={`Ganti kode ${detail?.name}?`}
        description="Kode lama dinonaktifkan permanen. Kode baru dibuat dengan profile yang sama; masa berlaku & kuota sesuai input admin."
        confirmLabel="Ya, Ganti Kode"
        onConfirm={doGanti}
        testid="pin-ganti-kode"
      />

      <PinConfirm
        open={pinOpen}
        onOpenChange={setPinOpen}
        title={`Lepas MAC dari ${detail?.name}?`}
        description={`MAC ${detail?.mac_address || ""} akan dilepas dan sesi aktif voucher diputus. Pelanggan bisa login ulang di perangkat baru.`}
        confirmLabel="Ya, Hapus MAC Address"
        destructive
        onConfirm={lepas}
        testid="pin-lepas-mac"
      />
    </div>
  );
}
